import { PayloadAction } from "@reduxjs/toolkit";
import toolIndex from "toolConstants";
import { reject, startsWith } from "lodash";

export const warehouseReducers = {
  /**
   * Sets the loading dock status for a specific side.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The side to toggle ('top_docks', 'right_docks', 'bottom_docks', 'left_docks').
   */
  setLoadingDocks: (state: any, action: any) => {
    const side = action?.payload;
    const curr = state.loadingDocks[side];
    if (side) {
      state.loadingDocks = { ...state.loadingDocks, [side]: !state.loadingDocks[side] };
    }
    if (curr) {
      state.ghostDocks = reject(state.ghostDocks, (dock: string) => startsWith(dock, side.split('_')[0]));
    };
  },

  /**
   * Sets the area to update.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The area update information.
   * @param {string} action.payload.id - The ID of the area to update.
   * @param {string} [action.payload.name] - The new name for the area.
   * @param {string} [action.payload.areaType] - The type of the area.
   */
  setAreaToUpdate: (state: any, action: PayloadAction<{ id: string; name?: string; areaType?: string } | { area: string; update: { parameter: string; value: any } }>) => {
    state.areaToUpdate = action.payload
  },

  /**
   * Sets the travel lane properties.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The travel lane properties.
   * @param {number} action.payload.width - The width of the travel lane.
   */
  setTravelLane: (state: any, action: any) => {
    state.travelLane = action.payload
  },

  /**
   * Toggles the ghost dock status for a specific dock.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The dock identifier to toggle.
   */
  setGhostDocks: (state: any, action: any) => {
    const current = [...state.ghostDocks];
    const dock = action.payload;
    current.includes(dock) ? current.splice(current.indexOf(dock), 1) : current.push(dock);
    state.ghostDocks = [...current]
  },

  /**
   * Sets the area usage for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The new area usage.
   */
  setAreaUsage: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].areaUsage = action.payload;
    // toolIndex[state.currentTool].resetContents(state);
    toolIndex[state.currentTool].resetArea(state);
  },

  /**
   * Sets the storage orientation for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The new storage orientation.
   */
  setStorageOrientation: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].storageOrientation = action.payload
    toolIndex[state.currentTool].resetArea(state);
  },

  /**
    * Sets the upright height in feet for the current area.
    * @param {Object} state - The current state.
    * @param {Object} action - The action object.
    * @param {number} action.payload - The new upright height in feet.
    */
  setUprightHeightFt: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].uprightHeightFt = parseInt(action.payload)
  },

  /**
   * Sets the number of picking aisles for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {number} action.payload - The new number of picking aisles.
   */
  setPickingAisles: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].pickingAisles = action.payload
  },

  /**
   * Sets the number of primary aisles for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {number|string} action.payload - The new number of primary aisles.
   */
  setPrimaryAisles: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].primaryAisles = (isNaN(action.payload) ? '' : action.payload)
  },

  /**
   * Sets the number of connector aisles for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {number} action.payload - The new number of connector aisles.
   */
  setConnectorAisles: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].connectorAisles = action.payload
    state.areas[state.activeTab].pickingAisles = 0
  },

  /**
   * Sets the storage ready status for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {boolean} action.payload - The new storage ready status.
   */
  setStorageReady: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    state.areas[state.activeTab].storageReady = action.payload
  },

  /**
   * Sets the rack arrays for a specific area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The rack arrays information.
   * @param {string} action.payload.area - The ID of the area to update.
   * @param {Array} action.payload.arrays - The new rack arrays.
   */
  setRackArrays: (state: any, action: any) => {
    const area = action.payload.area
    state.areas[area].rackArrays = action.payload.arrays
  },

  /**
   * Sets the aisles array for a specific area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The aisles array information.
   * @param {string} action.payload.area - The ID of the area to update.
   * @param {Array} action.payload.array - The new aisles array.
   */
  setAislesArray: (state: any, action: any) => {
    // if (!(state.activeTab in state.areas)) return;
    const { area, array } = action.payload;
    state.areas[area].aislesArray = array
  },

  /**
   * Sets the coverage properties for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new coverage properties.
   */
  setCoverage: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    // Fix: Update specific properties without replacing entire area object
    const currentArea = state.areas[state.activeTab];
    currentArea.coverage = { ...currentArea.coverage, ...action.payload };
    currentArea.speakerAiming = 'aimAuto';
    currentArea.customized = { ...currentArea.customized, qty: false };
  },

  /**
   * Sets the rack fill for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new rack fill data.
   */
  setRackFill: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    if (action.payload) state.areas[state.activeTab].rackFill = action.payload
  },
};
